import { useState, useEffect, useRef } from 'react';
import { 
  consultasService as consultaService, 
  pacientesService as pacienteService,
  relatoriosService 
} from '../services/api';

// Declare a interface global corretamente
declare global {
  interface Window {
    Chart: any;
  }
}

// Interfaces específicas para relatórios
interface RelatorioConsultas {
  totalConsultas: number;
  consultasRealizadas: number;
  consultasCanceladas: number;
  consultasPendentes: number;
  taxaComparecimento: number;
  distribuicaoPorServico: Array<{nome: string, quantidade: number, percentual: number}>;
  consultasPorMes: Array<{mes: string, realizadas: number, canceladas: number, pendentes: number}>;
  consultasPorDiaSemana: Array<{dia: string, quantidade: number}>;
  horariosMaisAtivos: Array<{horario: string, quantidade: number}>;
}

interface RelatorioFinanceiro {
  totalRecebido: number;
  totalPendente: number;
  totalCancelado: number;
  receitaPorMes: Array<{mes: string, recebido: number, pendente: number}>;
  formasPagamento: Array<{forma: string, valor: number, quantidade: number}>;
  ticketMedio: number;
  pacientesMaisRentaveis: Array<{nome: string, valorTotal: number, consultasRealizadas: number}>;
  inadimplencia: {
    valorTotal: number;
    quantidade: number;
    pacientes: Array<{nome: string, valor: number, consultas: number}>
  };
}

interface FiltrosRelatorio {
  dataInicio: string;
  dataFim: string;
  servico: string;
  statusConsulta: string;
  statusPagamento: string;
}

export default function Relatorios() {
  const [carregando, setCarregando] = useState(true);
  const [abaAtiva, setAbaAtiva] = useState('consultas');
  const [erro, setErro] = useState<string | null>(null);
  
  // Estados dos relatórios
  const [relatorioConsultas, setRelatorioConsultas] = useState<RelatorioConsultas>({
    totalConsultas: 0,
    consultasRealizadas: 0,
    consultasCanceladas: 0,
    consultasPendentes: 0,
    taxaComparecimento: 0,
    distribuicaoPorServico: [],
    consultasPorMes: [],
    consultasPorDiaSemana: [],
    horariosMaisAtivos: []
  });
  
  const [relatorioFinanceiro, setRelatorioFinanceiro] = useState<RelatorioFinanceiro>({
    totalRecebido: 0,
    totalPendente: 0,
    totalCancelado: 0,
    receitaPorMes: [],
    formasPagamento: [],
    ticketMedio: 0,
    pacientesMaisRentaveis: [],
    inadimplencia: {
      valorTotal: 0,
      quantidade: 0,
      pacientes: []
    }
  });
  
  // Estados dos filtros com valores padrão para últimos 6 meses
  const [filtros, setFiltros] = useState<FiltrosRelatorio>(() => {
    const hoje = new Date();
    const seiseMesesAtras = new Date();
    seiseMesesAtras.setMonth(hoje.getMonth() - 6);
    
    // Adicionar 1 ano à data final para incluir consultas futuras já agendadas
    const umAnoFuturo = new Date();
    umAnoFuturo.setFullYear(hoje.getFullYear() + 1);
    
    return {
      dataInicio: seiseMesesAtras.toISOString().split('T')[0],
      dataFim: umAnoFuturo.toISOString().split('T')[0], // Incluir 1 ano à frente
      servico: '',
      statusConsulta: '',
      statusPagamento: ''
    };
  });
  
  // Refs para os gráficos
  const chartConsultasMes = useRef<any>(null);
  const chartDistribuicaoServico = useRef<any>(null);
  const chartReceitaMensal = useRef<any>(null);
  const chartFormasPagamento = useRef<any>(null);
  
  // Carregar dados dos relatórios CORRIGIDO
  const carregarRelatorios = async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      // console.log('[RELATORIOS] Carregando dados com filtros:', filtros);
      
      // CORREÇÃO: Usar relatoriosService em vez de fetch direto
      const [consultasData, financeiroData] = await Promise.all([
        relatoriosService.obterRelatorioConsultas({
          dataInicio: filtros.dataInicio,
          dataFim: filtros.dataFim,
          ...(filtros.servico && { servico: filtros.servico }),
          ...(filtros.statusConsulta && { statusConsulta: filtros.statusConsulta }),
        }),
        
        relatoriosService.obterRelatorioFinanceiro({
          dataInicio: filtros.dataInicio,
          dataFim: filtros.dataFim,
          ...(filtros.statusPagamento && { statusPagamento: filtros.statusPagamento }),
        })
      ]);
      
      setRelatorioConsultas(consultasData);
      setRelatorioFinanceiro(financeiroData);
      
    } catch (error) {
      console.error('[RELATORIOS] Erro ao carregar:', error);
      setErro('Não foi possível carregar os relatórios. Verifique sua conexão e tente novamente.');
    } finally {
      setCarregando(false);
    }
  };
  
  // Carregar dados quando filtros mudarem
  useEffect(() => {
    carregarRelatorios();
  }, [filtros]);
  
  // Inicializar gráficos quando dados carregarem ou quando mudar de aba
  useEffect(() => {
    if (!carregando && window.Chart) {
      // Pequeno delay para garantir que o DOM esteja pronto após mudança de aba
      setTimeout(initCharts, 100);
    }
  }, [carregando, relatorioConsultas, relatorioFinanceiro, abaAtiva]);
  
  // Função para inicializar gráficos
  const initCharts = () => {
    // Destruir gráficos existentes
    [chartConsultasMes, chartDistribuicaoServico, chartReceitaMensal, chartFormasPagamento]
      .forEach(ref => {
        if (ref.current) {
          ref.current.destroy();
          ref.current = null;
        }
      });
    
    // Gráficos da aba Consultas
    if (abaAtiva === 'consultas' || abaAtiva === 'todos') {
      // Gráfico de consultas por mês
      const ctxConsultas = document.getElementById('chart-consultas-mes');
      if (ctxConsultas) {
        chartConsultasMes.current = new window.Chart(ctxConsultas, {
          type: 'bar',
          data: {
            labels: relatorioConsultas.consultasPorMes.map(item => item.mes),
            datasets: [
              {
                label: 'Realizadas',
                data: relatorioConsultas.consultasPorMes.map(item => item.realizadas),
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                borderWidth: 1
              },
              {
                label: 'Canceladas',
                data: relatorioConsultas.consultasPorMes.map(item => item.canceladas),
                backgroundColor: '#dc3545',
                borderColor: '#dc3545',
                borderWidth: 1
              },
              {
                label: 'Pendentes',
                data: relatorioConsultas.consultasPorMes.map(item => item.pendentes),
                backgroundColor: '#ffc107',
                borderColor: '#ffc107',
                borderWidth: 1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { stacked: true },
              y: { 
                stacked: true,
                beginAtZero: true
              }
            }
          }
        });
      }
      
      // Gráfico de distribuição por serviço
      const ctxServicos = document.getElementById('chart-distribuicao-servico');
      if (ctxServicos && relatorioConsultas.distribuicaoPorServico.length > 0) {
        chartDistribuicaoServico.current = new window.Chart(ctxServicos, {
          type: 'doughnut',
          data: {
            labels: relatorioConsultas.distribuicaoPorServico.map(item => item.nome),
            datasets: [{
              data: relatorioConsultas.distribuicaoPorServico.map(item => item.quantidade),
              backgroundColor: [
                '#007bff', '#28a745', '#dc3545', '#ffc107', '#6f42c1',
                '#fd7e14', '#20c997', '#e83e8c', '#6c757d', '#17a2b8'
              ]
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
              position: 'bottom'
            }
          }
        });
      }
    }
    
    // Gráficos da aba Financeiro
    if (abaAtiva === 'financeiro' || abaAtiva === 'todos') {
      // Gráfico de receita mensal
      const ctxReceita = document.getElementById('chart-receita-mensal');
      if (ctxReceita) {
        chartReceitaMensal.current = new window.Chart(ctxReceita, {
          type: 'line',
          data: {
            labels: relatorioFinanceiro.receitaPorMes.map(item => item.mes),
            datasets: [
              {
                label: 'Recebido',
                data: relatorioFinanceiro.receitaPorMes.map(item => item.recebido),
                borderColor: '#28a745',
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                fill: true,
                tension: 0.1
              },
              {
                label: 'Pendente',
                data: relatorioFinanceiro.receitaPorMes.map(item => item.pendente),
                borderColor: '#ffc107',
                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                fill: true,
                tension: 0.1
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value: any) {
                    return 'R$ ' + Number(value).toLocaleString('pt-BR');
                  }
                }
              }
            },
            tooltips: {
              callbacks: {
                label: function(tooltipItem: any, data: any) {
                  return data.datasets[tooltipItem.datasetIndex].label + 
                         ': R$ ' + Number(tooltipItem.value).toLocaleString('pt-BR');
                }
              }
            }
          }
        });
      }
      
      // Gráfico de formas de pagamento
      const ctxPagamento = document.getElementById('chart-formas-pagamento');
      if (ctxPagamento && relatorioFinanceiro.formasPagamento.length > 0) {
        chartFormasPagamento.current = new window.Chart(ctxPagamento, {
          type: 'pie',
          data: {
            labels: relatorioFinanceiro.formasPagamento.map(item => 
              item.forma.charAt(0).toUpperCase() + item.forma.slice(1)
            ),
            datasets: [{
              data: relatorioFinanceiro.formasPagamento.map(item => item.valor),
              backgroundColor: ['#007bff', '#28a745', '#dc3545', '#ffc107', '#6f42c1']
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
              position: 'bottom'
            },
            tooltips: {
              callbacks: {
                label: function(tooltipItem: any, data: any) {
                  const value = data.datasets[0].data[tooltipItem.index];
                  return data.labels[tooltipItem.index] + 
                         ': R$ ' + Number(value).toLocaleString('pt-BR');
                }
              }
            }
          }
        });
      }
    }
  };
  
  // Função para aplicar filtros rápidos
  const aplicarFiltroRapido = (tipo: string) => {
    const hoje = new Date();
    let dataInicio = new Date();
    let dataFim = new Date();
    
    // Data fim padrão é 1 ano no futuro para consultas agendadas
    dataFim.setFullYear(hoje.getFullYear() + 1);
    
    switch (tipo) {
      case 'hoje':
        dataInicio = hoje;
        dataFim = hoje;
        break;
      case 'sete_dias':
        dataInicio.setDate(hoje.getDate() - 7);
        break;
      case 'mes_atual':
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0); // Último dia do mês atual
        break;
      case 'mes_anterior':
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
        dataFim = new Date(hoje.getFullYear(), hoje.getMonth(), 0); // Último dia do mês anterior
        break;
      case 'tres_meses':
        dataInicio.setMonth(hoje.getMonth() - 3);
        break;
      case 'seis_meses':
        dataInicio.setMonth(hoje.getMonth() - 6);
        break;
      case 'ano_atual':
        dataInicio = new Date(hoje.getFullYear(), 0, 1); // 1º de janeiro
        dataFim = new Date(hoje.getFullYear(), 11, 31); // 31 de dezembro
        break;
      case 'ano_anterior':
        dataInicio = new Date(hoje.getFullYear() - 1, 0, 1); // 1º de janeiro do ano anterior
        dataFim = new Date(hoje.getFullYear() - 1, 11, 31); // 31 de dezembro do ano anterior
        break;
      case 'proximo_mes':
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
        dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 2, 0);
        break;
      case 'todos':
        dataInicio = new Date(2000, 0, 1); // Data bem antiga
        break;
      default:
        return; // Sair se não for um tipo conhecido
    }
    
    setFiltros(prev => ({
      ...prev,
      dataInicio: dataInicio.toISOString().split('T')[0],
      dataFim: dataFim.toISOString().split('T')[0]
    }));
  };
  
  // Handlers para filtros
  const handleFiltroChange = (campo: keyof FiltrosRelatorio, valor: string) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };
  
  const resetarFiltros = () => {
    const hoje = new Date();
    const seiseMesesAtras = new Date();
    seiseMesesAtras.setMonth(hoje.getMonth() - 6);
    
    setFiltros({
      dataInicio: seiseMesesAtras.toISOString().split('T')[0],
      dataFim: hoje.toISOString().split('T')[0],
      servico: '',
      statusConsulta: '',
      statusPagamento: ''
    });
  };
  
  if (carregando) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="sr-only">Carregando...</span>
          </div>
          <p className="text-muted">Processando relatórios...</p>
        </div>
      </div>
    );
  }
  
  if (erro) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Erro ao carregar relatórios</h4>
        <p>{erro}</p>
        <hr />
        <button className="btn btn-primary" onClick={carregarRelatorios}>
          <i className="fas fa-sync mr-2"></i>
          Tentar Novamente
        </button>
      </div>
    );
  }
  
  return (
    <>
      {/* Filtros */}
      <div className="card card-primary card-outline">
        <div className="card-header">
          <h3 className="card-title">
            <i className="fas fa-filter mr-2"></i>
            Filtros de Período
          </h3>
          <div className="card-tools">
            <button type="button" className="btn btn-tool" data-card-widget="collapse">
              <i className="fas fa-minus"></i>
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-3">
              <div className="form-group">
                <label htmlFor="dataInicio">
                  <i className="fas fa-calendar-alt mr-1"></i>
                  Data Início
                </label>
                <input
                  type="date"
                  id="dataInicio"
                  className="form-control"
                  value={filtros.dataInicio}
                  onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="form-group">
                <label htmlFor="dataFim">
                  <i className="fas fa-calendar-alt mr-1"></i>
                  Data Fim
                </label>
                <input
                  type="date"
                  id="dataFim"
                  className="form-control"
                  value={filtros.dataFim}
                  onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label htmlFor="servico">
                  <i className="fas fa-stethoscope mr-1"></i>
                  Serviço
                </label>
                <select
                  id="servico"
                  className="form-control"
                  value={filtros.servico}
                  onChange={(e) => handleFiltroChange('servico', e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="psicoterapia">Psicoterapia</option>
                  <option value="avaliacao">Avaliação</option>
                  <option value="orientacao">Orientação</option>
                  <option value="terapia_casal">Terapia de Casal</option>
                  <option value="triagem">Triagem</option>
                </select>
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <label htmlFor="statusConsulta">
                  <i className="fas fa-check-circle mr-1"></i>
                  Status
                </label>
                <select
                  id="statusConsulta"
                  className="form-control"
                  value={filtros.statusConsulta}
                  onChange={(e) => handleFiltroChange('statusConsulta', e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="agendada">Agendada</option>
                  <option value="realizada">Realizada</option>
                  <option value="cancelada">Cancelada</option>
                  <option value="faltou">Faltou</option>
                </select>
              </div>
            </div>
            <div className="col-md-2">
              <div className="form-group">
                <div className="form-label">&nbsp;</div>
                <div>
                  <button
                    type="button"
                    className="btn btn-default btn-block"
                    onClick={resetarFiltros}
                  >
                    <i className="fas fa-undo mr-1"></i>
                    Resetar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* NOVA SEÇÃO: Filtros rápidos */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="d-block mb-2 form-label">
                <i className="fas fa-bolt mr-1"></i>
                Filtros Rápidos
              </div>
              <div className="btn-group btn-group-sm flex-wrap" role="group">
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('hoje')}>
                  <i className="fas fa-calendar-day mr-1"></i> Hoje
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('sete_dias')}>
                  <i className="fas fa-calendar-week mr-1"></i> Últimos 7 dias
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('mes_atual')}>
                  <i className="fas fa-calendar mr-1"></i> Mês Atual
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('mes_anterior')}>
                  <i className="fas fa-calendar-minus mr-1"></i> Mês Anterior
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('proximo_mes')}>
                  <i className="fas fa-calendar-plus mr-1"></i> Próximo Mês
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('tres_meses')}>
                  <i className="fas fa-calendar-alt mr-1"></i> Últimos 3 meses
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('seis_meses')}>
                  <i className="fas fa-calendar-alt mr-1"></i> Últimos 6 meses
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('ano_atual')}>
                  <i className="far fa-calendar-alt mr-1"></i> Ano Atual
                </button>
                <button type="button" className="btn btn-outline-primary m-1" onClick={() => aplicarFiltroRapido('todos')}>
                  <i className="fas fa-infinity mr-1"></i> Todos os registros
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="card card-primary card-tabs">
        <div className="card-header p-0 pt-1">
          <ul className="nav nav-tabs" id="custom-tabs-relatorios" role="tablist">
            <li className="nav-item">
              <a
                className={`nav-link ${abaAtiva === 'consultas' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('consultas')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaAtiva('consultas');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fas fa-calendar-check mr-2"></i>
                Consultas & Atendimentos
              </a>
            </li>
            <li className="nav-item">
              <a
                className={`nav-link ${abaAtiva === 'financeiro' ? 'active' : ''}`}
                onClick={() => setAbaAtiva('financeiro')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setAbaAtiva('financeiro');
                  }
                }}
                role="tab"
                style={{ cursor: 'pointer' }}
                tabIndex={0}
              >
                <i className="fas fa-dollar-sign mr-2"></i>
                Relatório Financeiro
              </a>
            </li>
          </ul>
        </div>
        <div className="card-body">
          
          {/* Aba Consultas */}
          {abaAtiva === 'consultas' && (
            <>
              {/* KPIs de Consultas */}
              <div className="row">
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-info">
                    <div className="inner">
                      <h3>{relatorioConsultas.totalConsultas}</h3>
                      <p>Total de Consultas</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-calendar-alt"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-success">
                    <div className="inner">
                      <h3>{relatorioConsultas.consultasRealizadas}</h3>
                      <p>Consultas Realizadas</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-warning">
                    <div className="inner">
                      <h3>{relatorioConsultas.consultasPendentes}</h3>
                      <p>Consultas Pendentes</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-clock"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-danger">
                    <div className="inner">
                      <h3>{relatorioConsultas.taxaComparecimento.toFixed(1)}%</h3>
                      <p>Taxa de Comparecimento</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-chart-line"></i>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Gráficos de Consultas */}
              <div className="row">
                <div className="col-md-8">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-chart-bar mr-2"></i>
                        Consultas por Mês
                      </h3>
                    </div>
                    <div className="card-body">
                      <div className="chart">
                        <canvas id="chart-consultas-mes" style={{ height: '300px' }}></canvas>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-chart-pie mr-2"></i>
                        Distribuição por Serviço
                      </h3>
                    </div>
                    <div className="card-body">
                      <div className="chart">
                        <canvas id="chart-distribuicao-servico" style={{ height: '300px' }}></canvas>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tabelas de Consultas */}
              <div className="row">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-calendar-week mr-2"></i>
                        Consultas por Dia da Semana
                      </h3>
                    </div>
                    <div className="card-body table-responsive p-0">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Dia da Semana</th>
                            <th className="text-right">Quantidade</th>
                          </tr>
                        </thead>
                        <tbody>
                          {relatorioConsultas.consultasPorDiaSemana.map((item, index) => (
                            <tr key={index}>
                              <td>{item.dia}</td>
                              <td className="text-right">
                                <span className="badge badge-primary">{item.quantidade}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-clock mr-2"></i>
                        Horários Mais Ativos
                      </h3>
                    </div>
                    <div className="card-body table-responsive p-0">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Horário</th>
                            <th className="text-right">Consultas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {relatorioConsultas.horariosMaisAtivos.slice(0, 10).map((item, index) => (
                            <tr key={index}>
                              <td>{item.horario}</td>
                              <td className="text-right">
                                <span className="badge badge-success">{item.quantidade}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Aba Financeiro */}
          {abaAtiva === 'financeiro' && (
            <>
              {/* KPIs Financeiros */}
              <div className="row">
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-success">
                    <div className="inner">
                      <h3>R$ {relatorioFinanceiro.totalRecebido.toLocaleString('pt-BR')}</h3>
                      <p>Total Recebido</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-dollar-sign"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-warning">
                    <div className="inner">
                      <h3>R$ {relatorioFinanceiro.totalPendente.toLocaleString('pt-BR')}</h3>
                      <p>Total Pendente</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-clock"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-info">
                    <div className="inner">
                      <h3>R$ {relatorioFinanceiro.ticketMedio.toLocaleString('pt-BR')}</h3>
                      <p>Ticket Médio</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-calculator"></i>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-6">
                  <div className="small-box bg-danger">
                    <div className="inner">
                      <h3>{relatorioFinanceiro.inadimplencia.quantidade}</h3>
                      <p>Pacientes Inadimplentes</p>
                    </div>
                    <div className="icon">
                      <i className="fas fa-exclamation-triangle"></i>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Gráficos Financeiros */}
              <div className="row">
                <div className="col-md-8">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-chart-line mr-2"></i>
                        Evolução da Receita
                      </h3>
                    </div>
                    <div className="card-body">
                      <div className="chart">
                        <canvas id="chart-receita-mensal" style={{ height: '300px' }}></canvas>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-credit-card mr-2"></i>
                        Formas de Pagamento
                      </h3>
                    </div>
                    <div className="card-body">
                      <div className="chart">
                        <canvas id="chart-formas-pagamento" style={{ height: '300px' }}></canvas>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tabelas Financeiras */}
              <div className="row">
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Inadimplência
                      </h3>
                    </div>
                    <div className="card-body table-responsive p-0">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Paciente</th>
                            <th className="text-right">Valor Pendente</th>
                            <th className="text-center">Consultas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {relatorioFinanceiro.inadimplencia.pacientes.slice(0, 10).map((item, index) => (
                            <tr key={index}>
                              <td>
                                {item.nome}
                                <span className="badge badge-secondary mr-2">{index + 1}°</span>
                              </td>
                              <td className="text-right text-danger font-weight-bold">
                                R$ {item.valor.toLocaleString('pt-BR')}
                              </td>
                              <td className="text-center">
                                <span className="badge badge-info">{item.consultas}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card">
                    <div className="card-header">
                      <h3 className="card-title">
                        <i className="fas fa-star mr-2"></i>
                        Top 10 Pacientes - Receita
                      </h3>
                    </div>
                    <div className="card-body table-responsive p-0">
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Paciente</th>
                            <th className="text-right">Receita Total</th>
                            <th className="text-center">Consultas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {relatorioFinanceiro.pacientesMaisRentaveis.slice(0, 10).map((item, index) => (
                            <tr key={index}>
                              <td>
                                {item.nome}
                                <span className="badge badge-secondary mr-2">{index + 1}°</span>
                              </td>
                              <td className="text-right text-success font-weight-bold">
                                R$ {item.valorTotal.toLocaleString('pt-BR')}
                              </td>
                              <td className="text-center">
                                <span className="badge badge-info">{item.consultasRealizadas}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

